// The authenticated console shell: header (who + logout) + role-scoped tabs.
// Tabs are computed from the operator's brand-mint client roles:
//   - Brands (the provisioning workflow) → customer-admin / field-engineer
//   - Users / Roles (identity admin)     → service-admin
//   - API (Swagger reference)            → any recognized role
// The UI only view-gates; the backend is the real enforcer for every action.
import { lazy, Suspense, useState } from "react";
import type { User } from "../auth";
import { displayName, logout, rolesFromUser } from "../auth";
import { Banner } from "./Banner";
import { BrandsTab } from "./BrandsTab";
import { RolesTab } from "./RolesTab";
import { UsersTab } from "./UsersTab";

// The API tab pulls in Swagger UI (~1 MB), so it is code-split and only loaded
// when the operator opens the tab — the rest of the console stays lightweight.
const ApiTab = lazy(() =>
  import("./ApiTab").then((m) => ({ default: m.ApiTab })),
);

// Prefixed brand-mint client roles (matching operatorauth + GET /v1/admin/roles).
const SERVICE_ADMIN = "brand-mint:service-admin";
const CUSTOMER_ADMIN = "brand-mint:customer-admin";
const FIELD_ENGINEER = "brand-mint:field-engineer";

type TabId = "brands" | "users" | "roles" | "api";

export function Console({ user }: { user: User }) {
  const roles = rolesFromUser(user);
  const isServiceAdmin = roles.includes(SERVICE_ADMIN);
  const canBrands =
    roles.includes(CUSTOMER_ADMIN) || roles.includes(FIELD_ENGINEER);

  const tabs: { id: TabId; label: string }[] = [];
  if (canBrands) tabs.push({ id: "brands", label: "Brands" });
  if (isServiceAdmin)
    tabs.push({ id: "users", label: "Users" }, { id: "roles", label: "Roles" });
  if (tabs.length > 0) tabs.push({ id: "api", label: "API" });

  const [tab, setTab] = useState<TabId>(tabs[0]?.id ?? "api");

  return (
    <>
      <header className="app-header">
        <h1>IoTFW Service Console</h1>
        <div className="who">
          <span>
            Signed in as <strong>{displayName(user)}</strong>
          </span>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void logout();
            }}
          >
            Log out
          </button>
        </div>
      </header>

      <main>
        {tabs.length === 0 ? (
          <Banner kind="error">
            Your account has no console role (service-admin, customer-admin or
            field-engineer), so the console is unavailable. (Roles on your token:{" "}
            {roles.length ? roles.join(", ") : "none"}.)
          </Banner>
        ) : (
          <>
            <div className="tabs" role="tablist" aria-label="Console sections">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  id={`tab-${t.id}`}
                  aria-selected={tab === t.id}
                  aria-controls={`panel-${t.id}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "brands" && (
              <div id="panel-brands" role="tabpanel" aria-labelledby="tab-brands">
                <BrandsTab />
              </div>
            )}
            {tab === "users" && (
              <div id="panel-users" role="tabpanel" aria-labelledby="tab-users">
                <UsersTab />
              </div>
            )}
            {tab === "roles" && (
              <div id="panel-roles" role="tabpanel" aria-labelledby="tab-roles">
                <RolesTab />
              </div>
            )}
            {tab === "api" && (
              <div id="panel-api" role="tabpanel" aria-labelledby="tab-api">
                <Suspense
                  fallback={<p className="muted">Loading API console…</p>}
                >
                  <ApiTab />
                </Suspense>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}
